import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Users, User, Gamepad2, Clock, Euro, CheckCircle, 
  ArrowRight, MapPin, Sparkles, MessageCircle, Phone
} from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Images androïdes IA générées par le client
const TRON_IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_c4b8efe0-887c-4c49-ab73-3162491c4d73/artifacts/5c52dzth_1FF6B37C-6ED9-4724-AEBD-F1C8DDD70C73.png",
  guide: "https://customer-assets.emergentagent.com/job_c4b8efe0-887c-4c49-ab73-3162491c4d73/artifacts/9ak2jpqm_1B94520E-3EBA-4CB1-9147-DE6B3C59B7DD.png",
  trainer: "https://customer-assets.emergentagent.com/job_c4b8efe0-887c-4c49-ab73-3162491c4d73/artifacts/dr6znd2r_4641A994-51F7-4AEA-824E-5315348DDDF0.png",
  secondary: "https://customer-assets.emergentagent.com/job_c4b8efe0-887c-4c49-ab73-3162491c4d73/artifacts/pnxjfnov_9EEA2BB1-33E3-4079-8844-3FDC343A1F3D.png",
  accent: "https://customer-assets.emergentagent.com/job_c4b8efe0-887c-4c49-ab73-3162491c4d73/artifacts/9ak2jpqm_1B94520E-3EBA-4CB1-9147-DE6B3C59B7DD.png"
};

const WHATSAPP_NUMBER = "+33644692901";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER.replace(/[+\s]/g, '')}?text=Bonjour Lucie-IA, je souhaite en savoir plus sur les formations IA de l'Académie IA.`;

const FormationsPage = () => {
  const formations = [
    {
      id: "masterclass_half",
      icon: <Users className="w-8 h-8" />,
      title: "Masterclass Découverte",
      subtitle: "Demi-journée",
      duration: "4 heures",
      participants: "5 à 15 personnes",
      price: "750€ HT",
      description: "Une introduction immersive à l'IA générative. Découvrez les fonctionnalités clés à travers des ateliers pratiques guidés.",
      includes: [
        "Présentation des outils majeurs (ChatGPT, Claude, Copilot...)",
        "3 ateliers pratiques hands-on",
        "Q&A avec l'expert",
        "Support post-formation"
      ],
      ideal: "Équipes souhaitant une première immersion dans l'IA",
      color: "#007AFF"
    },
    {
      id: "masterclass_full",
      icon: <Users className="w-8 h-8" />,
      title: "Masterclass Approfondie",
      subtitle: "Journée complète",
      duration: "7 heures",
      participants: "5 à 15 personnes",
      price: "1 500€ HT",
      description: "Une journée pour vraiment maîtriser l'IA. Découverte, expérimentation, et création d'outils personnalisés avec le formateur à vos côtés.",
      includes: [
        "Tour d'horizon complet des outils IA",
        "6+ ateliers pratiques progressifs",
        "Création d'outils IA personnalisés",
        "Session philosophique : impact et éthique",
        "Accompagnement de la courbe du deuil",
        "Support post-formation étendu"
      ],
      ideal: "Équipes prêtes à intégrer l'IA dans leur quotidien",
      color: "#007AFF",
      featured: true
    },
    {
      id: "individual",
      icon: <User className="w-8 h-8" />,
      title: "Formation Individuelle",
      subtitle: "Coaching personnalisé",
      duration: "2 heures / personne",
      participants: "1 personne",
      price: "Sur devis",
      description: "Formation sur mesure partant de vos besoins réels. Idéal pour les membres de la direction ou postes à responsabilité.",
      includes: [
        "Diagnostic de vos connaissances actuelles",
        "Exploration de votre métier et missions",
        "Création d'outils IA sur mesure",
        "Conseils personnalisés selon votre profil",
        "Sensibilisation à la sécurité des données"
      ],
      ideal: "Dirigeants, managers, postes clés",
      color: "#FFD700"
    },
    {
      id: "workshop_tour",
      icon: <Gamepad2 className="w-8 h-8" />,
      title: "Ateliers Découverte Ludiques",
      subtitle: "Partout en France",
      duration: "2 heures / atelier",
      participants: "Jusqu'à 1000 salariés",
      price: "Sur devis",
      description: "Initiation ludique avec jeux interactifs personnalisés. Le formateur se déplace dans vos locaux partout en France.",
      includes: [
        "Jeu interactif IA personnalisé à votre entreprise",
        "Chaque participant crée sa micro-app IA",
        "Adaptation au contexte de chaque filiale",
        "Photos et informations du management intégrées",
        "QR code d'accès individuel"
      ],
      ideal: "Grandes entreprises, tournées nationales",
      color: "#00D4AA"
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="formations-page">
      <Navigation />
      
      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-16 relative">
        <div className="hero-glow" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-white/5 border border-white/10 mb-4 sm:mb-6">
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-[#007AFF]" />
              <span className="text-xs sm:text-sm text-white/70">Entièrement réalisé par l'Intelligence Artificielle</span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-4 sm:mb-6">
              Nos <span className="text-[#007AFF]">Formations</span>
            </h1>
            <p className="text-base sm:text-xl text-white/60 leading-relaxed">
              Des formats adaptés à chaque besoin, animés par des experts 
              pédagogues qui répondent à toutes vos questions sur l'IA.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Formations Grid */}
      <section className="pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-4 sm:gap-8">
            {formations.map((formation, index) => (
              <motion.div
                key={formation.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-6 sm:p-8 relative overflow-hidden"
                data-testid={`formation-card-${formation.id}`}
              >
                {formation.featured && (
                  <div className="absolute top-4 right-4 px-3 py-1 bg-[#007AFF] rounded-full text-xs font-semibold">
                    RECOMMANDÉ
                  </div>
                )}

                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                  {/* Icon */}
                  <div 
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${formation.color}15`, color: formation.color }}
                  >
                    {formation.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                      <h3 className="text-xl sm:text-2xl font-bold text-white">{formation.title}</h3>
                      <span className="text-xs sm:text-sm text-white/50 px-2 py-1 bg-white/5 rounded-full">
                        {formation.subtitle}
                      </span>
                    </div>
                    
                    <p className="text-white/60 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
                      {formation.description}
                    </p>

                    {/* Meta */}
                    <div className="flex flex-wrap gap-3 sm:gap-4 mb-4 sm:mb-6">
                      <div className="flex items-center gap-2 text-white/70">
                        <Clock className="w-4 h-4" />
                        <span className="text-xs sm:text-sm">{formation.duration}</span>
                      </div>
                      <div className="flex items-center gap-2 text-white/70">
                        <Users className="w-4 h-4" />
                        <span className="text-xs sm:text-sm">{formation.participants}</span>
                      </div>
                      <div className="flex items-center gap-2 text-white/70">
                        <Euro className="w-4 h-4" />
                        <span className="text-xs sm:text-sm font-semibold" style={{ color: formation.color }}>
                          {formation.price}
                        </span>
                      </div>
                    </div>

                    {/* Includes */}
                    <div className="mb-4 sm:mb-6">
                      <p className="text-white/50 text-xs sm:text-sm mb-2 sm:mb-3">Ce qui est inclus :</p>
                      <div className="grid grid-cols-1 gap-1.5 sm:gap-2">
                        {formation.includes.slice(0, 4).map((item, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-white/70 text-xs sm:text-sm">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Ideal for */}
                    <p className="text-white/40 text-xs sm:text-sm mb-4 sm:mb-6">
                      <span className="font-medium">Idéal pour :</span> {formation.ideal}
                    </p>

                    {/* CTA */}
                    <Link
                      to={`/tunnel?format=${formation.id}`}
                      className="inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full font-medium transition-all hover:gap-3 text-sm"
                      style={{ 
                        background: formation.color,
                        color: formation.color === '#FFD700' ? '#000' : '#fff'
                      }}
                      data-testid={`formation-cta-${formation.id}`}
                    >
                      Demander un devis
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-20 border-t border-white/5" data-testid="formations-testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              Retours de nos <span className="text-[#007AFF]">stagiaires</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-6"
            >
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                "Notre COMEX de 12 personnes a suivi la masterclass journée complète. 
                Le moment sur la courbe du deuil face à l'IA a été particulièrement éclairant - 
                plusieurs d'entre nous étaient en phase de déni sans le savoir. Maintenant, 
                on en rit, mais surtout on avance !"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#007AFF] flex items-center justify-center text-white text-xs font-bold">CM</div>
                <div>
                  <p className="text-white text-sm font-medium">Caroline M.</p>
                  <p className="text-white/50 text-xs">Présidente, ETI Export 80% CA international</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass-card p-6"
            >
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                "La formation individuelle a été un déclic. En 2 heures, j'ai compris comment 
                l'IA pouvait transformer mes contrats de 40 pages en synthèses actionnables. 
                Mon efficacité a doublé sur la revue documentaire."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold">NR</div>
                <div>
                  <p className="text-white text-sm font-medium">Nicolas R.</p>
                  <p className="text-white/50 text-xs">Avocat associé, Cabinet d'affaires Paris</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* France Coverage Section with TRON image */}
      <section className="py-16 sm:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-6 sm:p-12 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#007AFF]/10 to-transparent" />
            
            <div className="relative grid md:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#007AFF]/20 text-[#007AFF] text-sm mb-4">
                  <MapPin className="w-4 h-4" />
                  Partout en France
                </div>
                
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
                  Nous venons à vous
                </h2>
                <p className="text-white/60 mb-6 leading-relaxed text-sm sm:text-base">
                  De Paris à Marseille, de Lille à Toulouse, nos formateurs se déplacent 
                  dans vos locaux pour des sessions personnalisées. Ateliers individuels 
                  ou découverte ludique selon vos besoins.
                </p>

                <div className="space-y-3 mb-6 sm:mb-8">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-[#007AFF]" />
                    <span className="text-white/70 text-sm">Lyon, Bordeaux, Nantes, Strasbourg...</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-[#007AFF]" />
                    <span className="text-white/70 text-sm">Jusqu'à 6 ateliers par jour</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-[#007AFF]" />
                    <span className="text-white/70 text-sm">Jeux interactifs personnalisés</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/tunnel?format=workshop_tour"
                    className="btn-primary inline-flex items-center justify-center gap-2 text-sm"
                    data-testid="tour-cta"
                  >
                    Réserver des créneaux
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a
                    href={WHATSAPP_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary inline-flex items-center justify-center gap-2 text-sm border-green-500/50 text-green-400 hover:bg-green-500/20 transition-all"
                    data-testid="whatsapp-cta"
                  >
                    <WhatsAppIcon className="w-5 h-5" />
                    <span className="font-semibold">Contactez Lucie-IA</span>
                  </a>
                </div>
              </div>

              <div className="relative hidden md:block">
                <img 
                  src={TRON_IMAGES.trainer}
                  alt="Formatrice IA - Votre guide"
                  className="w-full h-80 object-cover rounded-2xl"
                  style={{ objectPosition: 'center top' }}
                />
                <div className="absolute inset-0 rounded-2xl" style={{
                  boxShadow: 'inset 0 0 60px rgba(0, 200, 255, 0.15)',
                  pointerEvents: 'none'
                }} />
                <div className="absolute bottom-4 left-4 glass-card p-3">
                  <p className="text-white text-sm font-medium">Lucie-IA</p>
                  <p className="text-cyan-400 text-xs">Votre contact IA</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 border-t border-white/5" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              Questions <span className="text-[#007AFF]">fréquentes</span>
            </h2>
          </motion.div>

          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card p-5"
            >
              <h4 className="text-white font-semibold mb-2">Faut-il avoir des connaissances techniques pour suivre les formations ?</h4>
              <p className="text-white/60 text-sm">
                Absolument pas ! Nos formations sont conçues pour des publics non-techniques. 
                Nous partons de zéro et progressons ensemble, quelle que soit votre fonction dans l'entreprise.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass-card p-5"
            >
              <h4 className="text-white font-semibold mb-2">Les formations sont-elles éligibles au financement par les OPCO ?</h4>
              <p className="text-white/60 text-sm">
                Oui, nos formations peuvent être prises en charge par votre OPCO. 
                Nous vous accompagnons dans les démarches administratives et fournissons tous les documents nécessaires 
                (convention, programme, attestation de présence).
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass-card p-5"
            >
              <h4 className="text-white font-semibold mb-2">Combien de temps à l'avance faut-il réserver ?</h4>
              <p className="text-white/60 text-sm">
                Idéalement 2 à 3 semaines pour les masterclass, 1 semaine pour les formations individuelles. 
                Pour les tournées nationales avec ateliers ludiques, prévoyez 4 à 6 semaines selon le nombre de sites.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass-card p-5"
            >
              <h4 className="text-white font-semibold mb-2">Quel matériel est nécessaire pour les participants ?</h4>
              <p className="text-white/60 text-sm">
                Chaque participant doit disposer d'un ordinateur portable ou d'une tablette avec accès internet. 
                Nous fournissons les accès aux outils IA utilisés pendant la formation. 
                Un vidéoprojecteur est nécessaire pour les sessions en présentiel.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="glass-card p-5"
            >
              <h4 className="text-white font-semibold mb-2">Y a-t-il un suivi après la formation ?</h4>
              <p className="text-white/60 text-sm">
                Oui ! Chaque formation inclut un support post-formation : accès à une FAQ enrichie, 
                hotline email pendant 30 jours, et possibilité de sessions de suivi à tarif préférentiel. 
                Les clients grands comptes bénéficient d'un accès à notre portail post-formation personnalisé.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WhatsApp Contact Section */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-6 sm:p-8 text-center border-green-500/20 bg-green-500/5"
          >
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-8 h-8 text-green-500" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
              Une question ? <span className="text-green-400">Contactez Lucie-IA !</span>
            </h3>
            <p className="text-white/60 mb-6 text-sm sm:text-base">
              Réponse rapide garantie sur WhatsApp - Votre contact direct
            </p>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-green-500 text-white font-bold text-lg hover:bg-green-600 hover:scale-105 transition-all shadow-lg shadow-green-500/30"
              data-testid="whatsapp-main"
            >
              <WhatsAppIcon className="w-6 h-6" />
              <span>+33 6 44 69 29 01</span>
              <Phone className="w-5 h-5 animate-pulse" />
            </a>
            <p className="text-white/40 text-xs mt-4">
              lucie@academie-ia.fr
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FormationsPage;
